import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Fish, LogOut, ShieldCheck, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useAuth } from "../../context/AuthContext";
import { DiscordIcon } from "./icons";
import { toast } from "sonner";

export const CrownIcon = ({ className }) => (
  <svg
    viewBox="0 0 64 40"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinejoin="round"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M6 34 L3 12 L18 22 L32 4 L46 22 L61 12 L58 34 Z" />
    <circle cx="3" cy="10" r="2" />
    <circle cx="32" cy="3.5" r="2" />
    <circle cx="61" cy="10" r="2" />
  </svg>
);

export const Logo = ({ className = "" }) => (
  <Link
    to="/"
    aria-label="flounder king - home"
    className={`relative inline-flex items-baseline group ${className}`}
  >
    <span
      className="font-display font-bold text-white text-[1.35rem] sm:text-[1.45rem] leading-none tracking-tight"
      style={{ textShadow: "0 2px 0 rgba(50,125,190,0.4), 0 3px 10px rgba(3,20,42,0.55)" }}
    >
      flounder king
    </span>
    <CrownIcon className="absolute -top-2.5 -right-1 w-5 h-5 text-[#ffd94a] rotate-12 transition-transform duration-300 group-hover:-rotate-3" />
  </Link>
);

const NAV_ITEMS = [
  { to: "/", label: "Home", end: true },
  { to: "/raffles", label: "Raffles" },
  { to: "/leaderboard", label: "Leaderboard" },
];

const NavLinks = ({ className = "" }) => (
  <nav className={`flex items-center gap-4 sm:gap-6 ${className}`}>
    {NAV_ITEMS.map((item, i) => (
      <React.Fragment key={item.to}>
        {i > 0 && <Fish className="w-3.5 h-3.5 text-white/30" aria-hidden="true" />}
        <NavLink
          to={item.to}
          end={item.end}
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
        >
          {item.label}
        </NavLink>
      </React.Fragment>
    ))}
  </nav>
);

const AuthArea = () => {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <span className="w-9 h-9 rounded-full bg-white/10 animate-pulse" />;
  }

  if (!user) {
    return (
      <button type="button" onClick={login} className="btn-discord">
        <DiscordIcon className="w-4 h-4" />
        <span className="hidden sm:inline">Login with Discord</span>
        <span className="sm:hidden">Login</span>
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full bg-white/8 border border-white/15 hover:bg-white/15 hover:border-white/30 transition-colors duration-300"
        >
          <img
            src={user.avatar_url}
            alt={user.username}
            className="w-7 h-7 rounded-full object-cover"
          />
          <span className="hidden sm:block text-white text-[0.82rem] font-bold max-w-[110px] truncate">
            {user.global_name || user.username}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-white/60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#0a2e57] border-white/15 text-[#d7eafb] min-w-[190px] shadow-[0_20px_50px_rgba(2,20,44,0.6)]"
      >
        <DropdownMenuLabel className="text-[#9fc5e8] text-[0.7rem] tracking-[0.18em] uppercase">
          @{user.username}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {user.is_admin && (
          <DropdownMenuItem
            className="cursor-pointer focus:bg-white/10 focus:text-white"
            onClick={() => navigate("/admin")}
          >
            <ShieldCheck className="w-4 h-4 mr-2 text-[#7cf0cd]" />
            Admin Panel
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="cursor-pointer focus:bg-white/10 focus:text-white"
          onClick={() => {
            logout();
            toast.success("Logged out. See you in the reef!");
            navigate("/");
          }}
        >
          <LogOut className="w-4 h-4 mr-2 text-[#ff8ba0]" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname, location.hash]);

  return (
    <header
      className={`nav-shell fixed top-0 left-0 right-0 z-50 ${scrolled ? "nav-scrolled" : ""}`}
    >
      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <Logo />
        <NavLinks className="absolute left-1/2 -translate-x-1/2 hidden md:flex" />
        <AuthArea />
      </div>
      <div className="md:hidden flex justify-center pb-2.5 -mt-1">
        <NavLinks />
      </div>
    </header>
  );
};

export default Navbar;
